import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft, Heart, Target, Users, Globe, Award, Lightbulb, ArrowRight, MapPin, Building2 } from 'lucide-react';

const About = () => {
  const values = [
    {
      icon: Heart,
      title: "Accessibilité",
      description: "Rendre les soins de santé accessibles à tous, partout en Afrique, quelle que soit la localisation géographique."
    },
    {
      icon: Target,
      title: "Qualité",
      description: "Connecter les patients avec des professionnels de santé qualifiés et vérifiés pour des soins de qualité."
    },
    {
      icon: Users,
      title: "Humanité",
      description: "Placer l'humain au cœur de la technologie, en facilitant la relation patient-soignant."
    },
    {
      icon: Lightbulb,
      title: "Innovation",
      description: "Utiliser les dernières technologies pour améliorer continuellement l'expérience de santé."
    }
  ];

  const stats = [
    { value: "10 000+", label: "Patients inscrits" },
    { value: "500+", label: "Professionnels de santé" },
    { value: "50+", label: "Établissements partenaires" },
    { value: "2", label: "Villes couvertes" }
  ];

  const team = [
    {
      role: "Direction",
      description: "Une équipe passionnée par la santé et la technologie, avec une expertise combinée de plus de 20 ans dans le secteur de la santé en Afrique."
    },
    {
      role: "Développement",
      description: "Des ingénieurs talentueux qui construisent une plateforme robuste, sécurisée et adaptée aux réalités africaines."
    },
    {
      role: "Médical",
      description: "Des professionnels de santé qui veillent à la qualité et à la pertinence de nos services médicaux."
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/">
            <Button variant="ghost" className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Retour à l&apos;accueil
            </Button>
          </Link>
          <Link to="/" className="text-2xl font-bold text-[#E53935]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            DOCKHTA
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-gradient-to-br from-[#1A237E] to-[#E53935] text-white py-20">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            À propos de DOCKHTA
          </h1>
          <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
            DOCKHTA est né d&apos;une vision simple mais ambitieuse : démocratiser l&apos;accès aux soins de santé 
            en Afrique grâce au numérique.
          </p>
        </div>
      </section>

      {/* Mission */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-[#E53935]/10 rounded-xl flex items-center justify-center">
                  <Target className="w-6 h-6 text-[#E53935]" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Notre Mission
                </h2>
              </div>
              <p className="text-slate-600 leading-relaxed mb-6">
                Faciliter l&apos;accès aux soins de santé pour tous les Africains en connectant patients, 
                professionnels de santé et établissements sur une plateforme numérique moderne et accessible.
              </p>
              <p className="text-slate-600 leading-relaxed">
                Nous croyons que chaque personne mérite d&apos;avoir accès à des soins de qualité, 
                peu importe où elle se trouve. C&apos;est pourquoi nous développons des solutions 
                adaptées aux réalités du continent africain.
              </p>
            </div>
            <div className="bg-gradient-to-br from-slate-100 to-slate-200 rounded-2xl p-8">
              <div className="flex items-center gap-3 mb-4">
                <Globe className="w-6 h-6 text-[#1A237E]" />
                <h3 className="text-lg font-semibold text-slate-900">Notre Vision</h3>
              </div>
              <p className="text-slate-600 leading-relaxed">
                Devenir la plateforme de référence pour la santé numérique en Afrique, 
                en offrant une expérience de soins intégrée, de la prévention au suivi médical, 
                accessible depuis n&apos;importe quel smartphone.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-slate-900 text-center mb-12" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Nos Valeurs
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <Card key={index} className="border-0 shadow-sm text-center">
                  <CardContent className="p-6">
                    <div className="w-14 h-14 bg-[#E53935]/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                      <IconComponent className="w-7 h-7 text-[#E53935]" />
                    </div>
                    <h3 className="font-semibold text-slate-900 mb-2">{value.title}</h3>
                    <p className="text-sm text-slate-600">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-[#1A237E]">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-white text-center mb-12" style={{ fontFamily: 'Outfit, sans-serif' }}>
            DOCKHTA en chiffres
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <p className="text-4xl font-bold text-white mb-2">{stat.value}</p>
                <p className="text-white/70">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-slate-900 text-center mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Notre Équipe
          </h2>
          <p className="text-slate-600 text-center mb-12 max-w-2xl mx-auto">
            Une équipe pluridisciplinaire engagée pour révolutionner la santé en Afrique
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            {team.map((member, index) => (
              <Card key={index} className="border-0 shadow-sm">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-[#1A237E]/10 rounded-xl flex items-center justify-center mb-4">
                    <Users className="w-6 h-6 text-[#1A237E]" />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">{member.role}</h3>
                  <p className="text-sm text-slate-600">{member.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Location */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4">
          <div className="bg-gradient-to-r from-[#E53935]/5 to-[#E53935]/10 rounded-2xl p-8 md:p-12">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-4">
                  <Building2 className="w-6 h-6 text-[#E53935]" />
                  <h3 className="text-xl font-semibold text-slate-900">Nos bureaux</h3>
                </div>
                <p className="text-slate-600 mb-4">
                  DOCKHTA est fièrement développé au Cameroun, avec des bureaux à Yaoundé.
                  Notre objectif est de nous étendre progressivement à toute l&apos;Afrique francophone.
                </p>
                <div className="flex items-center gap-2 text-slate-600">
                  <MapPin className="w-5 h-5 text-[#E53935]" />
                  <span>Yaoundé, Cameroun</span>
                </div>
              </div>
              <div className="flex-shrink-0">
                <Award className="w-24 h-24 text-[#E53935]/20" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Rejoignez l&apos;aventure DOCKHTA
          </h2>
          <p className="text-slate-600 mb-8 max-w-2xl mx-auto">
            Que vous soyez patient, professionnel de santé ou établissement, 
            rejoignez-nous pour construire ensemble l&apos;avenir de la santé en Afrique.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button className="bg-[#E53935] hover:bg-[#D32F2F] h-12 px-8">
                Créer un compte
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="h-12 px-8">
                Nous contacter
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
